import { useState, useRef, useEffect } from 'react';
import { adminApi } from '../../services/api';
import toast from 'react-hot-toast';

interface Config {
  companyName: string;
  primaryColor: string;
  secondaryColor: string;
  welcomeMessage: string;
  maxFileSize: number;
  defaultExpiryHours: number;
  maxReadsDefault: number;
  logo?: string;
  smsVerificationEnabled?: boolean;
  brevoApiKey?: string;
  brevoSenderName?: string;
}

export default function SettingsPage() {
  const [config, setConfig] = useState<Config>({
    companyName: 'PowerSecret MSP',
    primaryColor: '#0066CC',
    secondaryColor: '#004999',
    welcomeMessage: 'Bienvenue sur notre plateforme securisee de depot d\'informations confidentielles.',
    maxFileSize: 1024,
    defaultExpiryHours: 168,
    maxReadsDefault: 1,
    logo: undefined,
    smsVerificationEnabled: false,
    brevoApiKey: '',
    brevoSenderName: 'PowerSecret',
  });
  const [showBrevoKey, setShowBrevoKey] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      const response = await adminApi.getConfig();
      if (response.data) {
        setConfig((prev) => ({ ...prev, ...response.data }));
        if (response.data.logo) {
          setLogoPreview(response.data.logo);
        }
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[Settings] handleLogoChange triggered');
    const file = e.target.files?.[0];
    console.log('[Settings] File selected:', file);
    if (!file) {
      console.log('[Settings] No file selected');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.log('[Settings] Invalid file type:', file.type);
      toast.error('Veuillez selectionner une image');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      console.log('[Settings] File too large:', file.size);
      toast.error('L\'image ne doit pas depasser 2 Mo');
      return;
    }

    console.log('[Settings] File is valid, reading...');
    // Create preview and convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      console.log('[Settings] File read complete, base64 length:', base64?.length);
      setLogoPreview(base64);
      setConfig((prev) => ({ ...prev, logo: base64 }));
      toast.success('Logo charge');
    };
    reader.onerror = (error) => {
      console.error('[Settings] FileReader error:', error);
      toast.error('Erreur lors de la lecture du fichier');
    };
    reader.readAsDataURL(file);
  };

  const handleLogoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez deposer une image');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('L\'image ne doit pas depasser 2 Mo');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setLogoPreview(base64);
      setConfig((prev) => ({ ...prev, logo: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setConfig((prev) => ({ ...prev, logo: undefined }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await adminApi.updateConfig(config as unknown as Record<string, unknown>);
      toast.success('Configuration enregistree');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Configuration</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branding */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Branding</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Nom de l'entreprise</label>
              <input
                type="text"
                value={config.companyName}
                onChange={(e) => setConfig({ ...config, companyName: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Logo</label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  logoPreview
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onClick={() => {
                  console.log('[Settings] Drop zone clicked, fileInputRef:', fileInputRef.current);
                  fileInputRef.current?.click();
                }}
                onDrop={handleLogoDrop}
                onDragOver={handleDragOver}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />

                {logoPreview ? (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-h-32 mx-auto rounded"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeLogo();
                      }}
                      className="absolute top-0 right-0 -mt-2 -mr-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Cliquez pour changer</p>
                  </div>
                ) : (
                  <>
                    <svg className="w-10 h-10 text-gray-400 dark:text-gray-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Glissez une image ou cliquez pour telecharger</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PNG, JPG, SVG (max 2 Mo)</p>
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Couleur principale</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config.primaryColor}
                    onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={config.primaryColor}
                    onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                    className="input flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="label">Couleur secondaire</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config.secondaryColor}
                    onChange={(e) => setConfig({ ...config, secondaryColor: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={config.secondaryColor}
                    onChange={(e) => setConfig({ ...config, secondaryColor: e.target.value })}
                    className="input flex-1"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="label">Message de bienvenue</label>
              <textarea
                value={config.welcomeMessage}
                onChange={(e) => setConfig({ ...config, welcomeMessage: e.target.value })}
                className="input min-h-[100px]"
              />
            </div>
          </div>
        </div>

        {/* Limits */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Limites et parametres</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Taille max des fichiers (Mo)</label>
              <input
                type="number"
                value={config.maxFileSize}
                onChange={(e) => setConfig({ ...config, maxFileSize: parseInt(e.target.value) || 0 })}
                className="input"
                min={1}
                max={2048}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum recommande : 1024 Mo (1 Go)</p>
            </div>
            <div>
              <label className="label">Expiration par defaut (heures)</label>
              <input
                type="number"
                value={config.defaultExpiryHours}
                onChange={(e) => setConfig({ ...config, defaultExpiryHours: parseInt(e.target.value) || 0 })}
                className="input"
                min={1}
                max={720}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">168 heures = 7 jours</p>
            </div>
            <div>
              <label className="label">Nombre de lectures par defaut</label>
              <input
                type="number"
                value={config.maxReadsDefault}
                onChange={(e) => setConfig({ ...config, maxReadsDefault: parseInt(e.target.value) || 0 })}
                className="input"
                min={1}
                max={10}
              />
            </div>
            <div>
              <label className="label">Types de fichiers autorises</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'zip', 'png', 'jpg'].map((type) => (
                  <span key={type} className="badge badge-neutral">
                    .{type}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SMS Verification (Brevo) */}
      <div className="mt-6">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Verification SMS</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Securisez la revelation des messages par SMS</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.smsVerificationEnabled}
                onChange={(e) => setConfig({ ...config, smsVerificationEnabled: e.target.checked })}
                className="sr-only peer"
                aria-label="Activer la verification SMS"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-500"></div>
            </label>
          </div>

          {config.smsVerificationEnabled && (
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configurez votre cle API Brevo pour activer la verification par SMS.
                Vous pouvez obtenir votre cle depuis{' '}
                <a
                  href="https://app.brevo.com/settings/keys/api"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:text-primary-600 underline"
                >
                  Brevo &gt; Parametres &gt; SMTP &amp; API
                </a>.
              </p>

              <div>
                <label className="label">Cle API Brevo</label>
                <div className="relative">
                  <input
                    type={showBrevoKey ? 'text' : 'password'}
                    value={config.brevoApiKey || ''}
                    onChange={(e) => setConfig({ ...config, brevoApiKey: e.target.value })}
                    className="input font-mono pr-10"
                    placeholder="xkeysib-xxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <button
                    type="button"
                    onClick={() => setShowBrevoKey(!showBrevoKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showBrevoKey ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="label">Nom de l'expediteur SMS</label>
                <input
                  type="text"
                  value={config.brevoSenderName || ''}
                  onChange={(e) => setConfig({ ...config, brevoSenderName: e.target.value })}
                  className="input"
                  placeholder="PowerSecret"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Nom affiche comme expediteur du SMS (max 11 caracteres)
                </p>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div className="text-sm text-red-800 dark:text-red-200">
                    <p className="font-medium">Securite</p>
                    <p className="mt-1">
                      La cle API sera stockee de maniere securisee.
                      Des frais SMS peuvent s'appliquer selon votre forfait Brevo.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      {logoPreview && (
        <div className="mt-6">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Apercu</h2>
            <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center gap-4">
                <img src={logoPreview} alt="Logo" className="h-12 object-contain" />
                <span className="text-xl font-bold" style={{ color: config.primaryColor }}>
                  {config.companyName}
                </span>
              </div>
              <p className="mt-4 text-gray-600 dark:text-gray-300">{config.welcomeMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Save button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn-primary"
        >
          {isSaving ? (
            <>
              <div className="spinner w-4 h-4 mr-2 border-white border-t-transparent" />
              Enregistrement...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Enregistrer les modifications
            </>
          )}
        </button>
      </div>
    </div>
  );
}
